import { useEffect, useState } from "react";

// vLOGIN-010
// login.staffiq.nl is centrale ingang: klantcode invullen en veilig doorsturen naar klant-subdomein.
// Oude klant-subdomeinen blijven werken zoals eerder.
// Tenant wordt automatisch gekozen op basis van het subdomein en vastgezet op klant-subdomeinen.
// bartels.staffiq.nl -> bartels
// hoeks.staffiq.nl   -> hoeks
// nautiq.staffiq.nl  -> nautic (interne tenantcode)
// nautic.staffiq.nl  -> nautic
// demo.staffiq.nl    -> demo
// partner.staffiq.nl -> partner
function isCentralLoginHost() {
  try {
    const host = String(window.location.hostname || "").toLowerCase().trim();
    return host === "login.staffiq.nl";
  } catch {
    return false;
  }
}

function tenantDomainFromInput(raw) {
  const value = String(raw || "").toLowerCase().trim();

  if (!value) return "";
  if (value === "crm") return "demo";
  if (value === "nautic") return "nautiq";

  return value.replace(/[^a-z0-9-]/g, "");
}

function detectTenantFromHost() {
  try {
    const host = String(window.location.hostname || "").toLowerCase().trim();
    const first = host.split(".")[0] || "";

    if (first === "bartels") return "bartels";
    if (first === "hoeks") return "hoeks";
    if (first === "partner") return "partner";
    if (first === "nautic" || first === "nautiq") return "nautic";
    if (first === "demo") return "demo";

    if (first === "login") {
      const stored =
        sessionStorage.getItem("staffiq_last_login_tenant") ||
        localStorage.getItem("staffiq_last_login_tenant") ||
        "";

      return stored;
    }

    const stored =
      sessionStorage.getItem("tenant") ||
      localStorage.getItem("tenant") ||
      "";

    return stored || "demo";
  } catch {
    return "demo";
  }
}

function isFixedTenantHost() {
  try {
    const host = String(window.location.hostname || "").toLowerCase().trim();
    const first = host.split(".")[0] || "";

    return ["bartels", "hoeks", "partner", "nautic", "nautiq", "demo"].includes(first);
  } catch {
    return false;
  }
}

function formatDateNL(iso) {
  const s = String(iso || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return "";
  const [y, m, d] = s.split("-");
  return `${d}-${m}-${y}`;
}

function taskGroup(task) {
  const today = new Date().toISOString().slice(0, 10);
  const due = String(task.due_date || "").slice(0, 10);

  if (!due) return "Zonder datum";
  if (due < today) return "Te laat";
  if (due === today) return "Vandaag";
  return "Binnenkort";
}

function groupColor(group) {
  if (group === "Te laat") return "#d64545";
  if (group === "Vandaag") return "#e67e22";
  if (group === "Binnenkort") return "#1f4e79";
  return "#777";
}

export default function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [tenant, setTenant] = useState(() => detectTenantFromHost());
  const fixedTenant = isFixedTenantHost();
  const [error, setError] = useState("");

  useEffect(() => {
    const detectedTenant = detectTenantFromHost();

    if (fixedTenant && tenant !== detectedTenant) {
      setTenant(detectedTenant);
    }
  }, [fixedTenant, tenant]);
  const [openTasks, setOpenTasks] = useState([]);
  const [currentUserId, setCurrentUserId] = useState("");
  const [showTaskPopup, setShowTaskPopup] = useState(false);

  async function loadOpenTasks(userId) {
    if (!userId) return [];

    try {
      const res = await fetch(`/api/users/${userId}/open-tasks`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("token") || ""}`,
        },
      });

      const data = await res.json().catch(() => []);

      if (!res.ok) return [];
      return Array.isArray(data) ? data : [];
    } catch {
      return [];
    }
  }

  async function markCompletedTasksSeen() {
    const ids = openTasks
      .filter((t) => t.popup_type === "completed_by_executor")
      .map((t) => t.id)
      .filter(Boolean);

    if (!currentUserId || ids.length === 0) return;

    try {
      await fetch(`/api/users/${currentUserId}/completed-tasks-seen`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("token") || ""}`,
        },
        body: JSON.stringify({ ids }),
      });
    } catch {
      // Geen blokkade voor inloggen als het afmelden van de popup niet lukt.
    }
  }

  async function continueLogin() {
    await markCompletedTasksSeen();
    setShowTaskPopup(false);
    onLogin();
  }

  async function openTasksModule() {
    await markCompletedTasksSeen();
    setShowTaskPopup(false);
    onLogin();

    setTimeout(() => {
      window.location.href = "/notities-taken";
    }, 100);
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    const cleanTenant = String(tenant || "").toLowerCase().trim();

    if (!cleanTenant) {
      setError("Vul eerst de klantcode in.");
      return;
    }

    if (isCentralLoginHost()) {
      const tenantDomain = tenantDomainFromInput(cleanTenant);

      if (!tenantDomain) {
        setError("Ongeldige klantcode.");
        return;
      }

      try {
        sessionStorage.setItem("staffiq_last_login_tenant", tenantDomain);
        localStorage.setItem("staffiq_last_login_tenant", tenantDomain);
      } catch {}

      window.location.href = `https://${tenantDomain}.staffiq.nl/`;
      return;
    }

    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ tenant: cleanTenant, email, password }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || "Login mislukt");
      }

      sessionStorage.setItem("token", data.token || "");
      sessionStorage.setItem("tenant", cleanTenant);
      sessionStorage.setItem("user", JSON.stringify(data.user || {}));

      localStorage.removeItem("token");
      localStorage.removeItem("tenant");
      localStorage.removeItem("user");

      setCurrentUserId(data.user?.id || "");
      const tasks = await loadOpenTasks(data.user?.id);
      setOpenTasks(tasks);

      if (tasks.length > 0) {
        setShowTaskPopup(true);
      } else {
        onLogin();
      }
    } catch (err) {
      setError(err.message || "Onjuiste login");
    }
  };

  const openTaskRows = openTasks.filter((t) => t.popup_type !== "completed_by_executor");
  const completedTaskRows = openTasks.filter((t) => t.popup_type === "completed_by_executor");

  const grouped = {
    "Te laat": openTaskRows.filter((t) => taskGroup(t) === "Te laat"),
    Vandaag: openTaskRows.filter((t) => taskGroup(t) === "Vandaag"),
    Binnenkort: openTaskRows.filter((t) => taskGroup(t) === "Binnenkort"),
    "Zonder datum": openTaskRows.filter((t) => taskGroup(t) === "Zonder datum"),
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "#f4f6f9",
        padding: 20,
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          width: "420px",
          padding: "40px",
          background: "white",
          borderRadius: "12px",
          boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
        }}
      >
        <h2 style={{ textAlign: "center", marginBottom: "30px" }}>
          CRM Login
        </h2>

        <form
          onSubmit={handleLogin}
          style={{ display: "flex", flexDirection: "column", gap: "15px" }}
        >
          <input
            type="text"
            name={`staffiq_tenant_${detectTenantFromHost()}`}
            placeholder={isCentralLoginHost() ? "Klantcode" : "Tenant"}
            autoComplete="new-password"
            value={tenant}
            readOnly={fixedTenant}
            onFocus={() => {
              if (fixedTenant) setTenant(detectTenantFromHost());
            }}
            onChange={(e) => {
              if (!fixedTenant) setTenant(e.target.value);
            }}
            required
            style={{
              padding: "12px",
              fontSize: "16px",
              background: fixedTenant ? "#f7f7f7" : "white",
            }}
          />

          <input
            type="email"
            placeholder="E-mail"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={{ padding: "12px", fontSize: "16px" }}
          />

          <input
            type="password"
            placeholder="Wachtwoord"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            style={{ padding: "12px", fontSize: "16px" }}
          />

          <button
            type="submit"
            style={{
              padding: "14px",
              fontSize: "16px",
              background: "#1f4e79",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
            }}
          >
            Inloggen
          </button>

          {error && (
            <p style={{ color: "red", textAlign: "center" }}>{error}</p>
          )}
        </form>
      </div>

      {showTaskPopup && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: 20,
            zIndex: 9999,
          }}
        >
          <div
            style={{
              width: "min(980px, 96vw)",
              maxHeight: "86vh",
              overflowY: "auto",
              background: "white",
              borderRadius: 14,
              boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
              padding: 22,
              boxSizing: "border-box",
            }}
          >
            <h2 style={{ marginTop: 0, marginBottom: 8 }}>
              Taken
            </h2>

            <div style={{ marginBottom: 16, opacity: 0.75 }}>
              Je hebt {openTaskRows.length} openstaande taak
              {openTaskRows.length === 1 ? "" : "en"}
              {completedTaskRows.length > 0
                ? ` en ${completedTaskRows.length} uitgevoerde taak${completedTaskRows.length === 1 ? "" : "en"} ter bevestiging.`
                : "."}
            </div>

            {Object.entries(grouped).map(([group, tasks]) => {
              if (!tasks.length) return null;

              return (
                <div key={group} style={{ marginBottom: 18 }}>
                  <h3
                    style={{
                      margin: "12px 0 8px 0",
                      color: groupColor(group),
                    }}
                  >
                    {group} ({tasks.length})
                  </h3>

                  <div style={{ overflowX: "auto" }}>
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        tableLayout: "fixed",
                        minWidth: 890,
                      }}
                    >
                      <colgroup>
                        <col style={{ width: 92 }} />
                        <col style={{ width: 70 }} />
                        <col style={{ width: 150 }} />
                        <col style={{ width: 155 }} />
                        <col style={{ width: 145 }} />
                        <col />
                        <col style={{ width: 130 }} />
                      </colgroup>

                      <thead>
                        <tr>
                          {[
                            "Datum",
                            "Tijd",
                            "Organisatie",
                            "Contactpersoon",
                            "Personeel",
                            "Titel",
                            "Gebruiker",
                          ].map((h) => (
                            <th
                              key={h}
                              style={{
                                textAlign: "left",
                                padding: "8px 8px",
                                borderBottom: "1px solid #ddd",
                                fontSize: 13,
                                whiteSpace: "nowrap",
                                verticalAlign: "bottom",
                              }}
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>

                      <tbody>
                        {tasks.map((t) => (
                          <tr key={t.id}>
                            <td style={{ padding: "7px 8px", borderBottom: "1px solid #eee", verticalAlign: "top", whiteSpace: "nowrap" }}>
                              {formatDateNL(t.due_date) || "-"}
                            </td>
                            <td style={{ padding: "7px 8px", borderBottom: "1px solid #eee", verticalAlign: "top", whiteSpace: "nowrap" }}>
                              {t.due_time || "00:00"}
                            </td>
                            <td style={{ padding: "7px 8px", borderBottom: "1px solid #eee", verticalAlign: "top", overflowWrap: "anywhere" }}>
                              {t.organisation_name || "-"}
                            </td>
                            <td style={{ padding: "7px 8px", borderBottom: "1px solid #eee", verticalAlign: "top", overflowWrap: "anywhere" }}>
                              {t.contact_name || "-"}
                            </td>
                            <td style={{ padding: "7px 8px", borderBottom: "1px solid #eee", verticalAlign: "top", overflowWrap: "anywhere" }}>
                              {t.personnel_name || "-"}
                            </td>
                            <td style={{ padding: "7px 8px", borderBottom: "1px solid #eee", verticalAlign: "top", overflowWrap: "anywhere" }}>
                              {t.title || "-"}
                            </td>
                            <td style={{ padding: "7px 8px", borderBottom: "1px solid #eee", verticalAlign: "top", overflowWrap: "anywhere" }}>
                              {t.assigned_user_name || "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}

            {completedTaskRows.length > 0 && (
              <div style={{ marginBottom: 18 }}>
                <h3
                  style={{
                    margin: "18px 0 8px 0",
                    color: "#198754",
                  }}
                >
                  Uitgevoerde taken ({completedTaskRows.length})
                </h3>

                <div style={{ marginBottom: 8, opacity: 0.75 }}>
                  Deze door jou aangemaakte taak
                  {completedTaskRows.length === 1 ? " is" : "en zijn"} door de uitvoerder op Klaar gezet.
                </div>

                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      tableLayout: "fixed",
                      minWidth: 890,
                    }}
                  >
                    <colgroup>
                      <col style={{ width: 92 }} />
                      <col style={{ width: 70 }} />
                      <col style={{ width: 150 }} />
                      <col style={{ width: 155 }} />
                      <col style={{ width: 145 }} />
                      <col />
                      <col style={{ width: 130 }} />
                    </colgroup>

                    <thead>
                      <tr>
                        {[
                          "Datum",
                          "Tijd",
                          "Organisatie",
                          "Contactpersoon",
                          "Personeel",
                          "Titel",
                          "Uitvoerder",
                        ].map((h) => (
                          <th
                            key={h}
                            style={{
                              textAlign: "left",
                              padding: "8px 8px",
                              borderBottom: "1px solid #ddd",
                              fontSize: 13,
                              whiteSpace: "nowrap",
                              verticalAlign: "bottom",
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>

                    <tbody>
                      {completedTaskRows.map((t) => (
                        <tr key={t.id}>
                          <td style={{ padding: "7px 8px", borderBottom: "1px solid #eee", verticalAlign: "top", whiteSpace: "nowrap" }}>
                            {formatDateNL(t.due_date) || "-"}
                          </td>
                          <td style={{ padding: "7px 8px", borderBottom: "1px solid #eee", verticalAlign: "top", whiteSpace: "nowrap" }}>
                            {t.due_time || "00:00"}
                          </td>
                          <td style={{ padding: "7px 8px", borderBottom: "1px solid #eee", verticalAlign: "top", overflowWrap: "anywhere" }}>
                            {t.organisation_name || "-"}
                          </td>
                          <td style={{ padding: "7px 8px", borderBottom: "1px solid #eee", verticalAlign: "top", overflowWrap: "anywhere" }}>
                            {t.contact_name || "-"}
                          </td>
                          <td style={{ padding: "7px 8px", borderBottom: "1px solid #eee", verticalAlign: "top", overflowWrap: "anywhere" }}>
                            {t.personnel_name || "-"}
                          </td>
                          <td style={{ padding: "7px 8px", borderBottom: "1px solid #eee", verticalAlign: "top", overflowWrap: "anywhere" }}>
                            {t.title || "-"}
                          </td>
                          <td style={{ padding: "7px 8px", borderBottom: "1px solid #eee", verticalAlign: "top", overflowWrap: "anywhere" }}>
                            {t.assigned_user_name || "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div
              style={{
                display: "flex",
                gap: 10,
                justifyContent: "flex-end",
                marginTop: 20,
              }}
            >
              <button
                onClick={continueLogin}
                style={{
                  padding: "10px 14px",
                  border: "1px solid #ccc",
                  borderRadius: 6,
                  background: "white",
                  cursor: "pointer",
                }}
              >
                Sluiten
              </button>

              <button
                onClick={openTasksModule}
                style={{
                  padding: "10px 14px",
                  border: "none",
                  borderRadius: 6,
                  background: "#1f4e79",
                  color: "white",
                  cursor: "pointer",
                }}
              >
                Open Notities/Taken
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}